import React from 'react';
import { Card, CardContent } from './ui/card';
import { Target, Heart, Users, Building2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Homepage "Who We Serve" gateway.
 *
 * Replaces `AudienceSegmentation` on the homepage only (the original is kept
 * intact for any other surface that still imports it). Each card now LINKS
 * to a dedicated audience landing page (`/athletes`, `/parents`, `/coaches`,
 * `/schools`) instead of all four scrolling to a Calendly link. This is the
 * core IA shift behind the Phase 22 restructure: homepage is a gateway,
 * audience pages own the depth.
 */
const audiences = [
  {
    id: 'athletes',
    href: '/athletes',
    icon: Target,
    title: 'Athletes',
    blurb: 'Middle school, high school & college athletes who refuse to let nerves, doubt, or mental blocks hold them back.',
  },
  {
    id: 'parents',
    href: '/parents',
    icon: Heart,
    title: 'Parents',
    blurb: 'Support your athlete’s mental game without adding pressure. Tools, language, and consultation.',
  },
  {
    id: 'coaches',
    href: '/coaches',
    icon: Users,
    title: 'Coaches',
    blurb: 'Build a mentally tough program. Frameworks, workshops, and consultation for coaching staffs.',
  },
  {
    id: 'schools',
    href: '/schools',
    icon: Building2,
    title: 'Schools & Clubs',
    blurb: 'Program-wide mental performance training for athletic departments, club programs, and travel teams.',
  },
];

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
};

export const AudienceGateway = () => {
  return (
    <section
      id="audience"
      className="py-16 sm:py-20 bg-white"
      data-testid="audience-gateway-section"
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <motion.div {...fadeIn} className="max-w-[60ch] mb-10">
          <div className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#004C54] font-bold mb-3">
            Who We Serve
          </div>
          <h2 className="font-heading text-[1.85rem] sm:text-[2.25rem] font-bold tracking-tight text-[#0a0d0e] mb-3">
            Pick your starting point.
          </h2>
          <p className="text-[15.5px] text-[#3a3a3a] leading-[1.55]">
            Mental performance training shows up differently for athletes, parents, coaches, and program leaders. Choose the path that fits.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {audiences.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.a
                key={a.id}
                href={a.href}
                {...fadeIn}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                data-testid={`audience-gateway-card-${a.id}`}
                className="block group"
              >
                <Card className="h-full border border-[#004C54]/12 hover:border-[#004C54]/40 hover:shadow-lg transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#004C54] mb-4">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-heading text-[18px] font-bold text-[#0a0d0e] mb-2">
                      {a.title}
                    </h3>
                    <p className="text-[14px] text-[#3a3a3a] leading-[1.55] mb-5">
                      {a.blurb}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#004C54] group-hover:text-[#003a40] transition-colors">
                      Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </CardContent>
                </Card>
              </motion.a>
            );
          })}
        </div>
        <p className="mt-6 text-[15.5px] text-[#3a3a3a] leading-[1.55]" data-testid="audience-gateway-schools-entry">
          <a href="/schools" className="text-[#004C54] font-medium hover:underline" data-testid="audience-gateway-schools-link">
            Leading a program? See how Apex works with schools, clubs and athletic departments →
          </a>
        </p>
      </div>
    </section>
  );
};

export default AudienceGateway;
